<template>
  <b-overlay
    :show="$store.state.overlays.pageHeader"
    variant="dark"
    opacity="0.5"
    blur=""
    no-fade
    wrap-tag="section"
    class="page-header"
    z-index="0"
    :class="{'local-placement': localPlacement, hidden: !(forceDisplay || isPageHeaderDisplayed) }"
  >
    <template #overlay><span> </span></template>
    <div class="title">
      <b-skeleton
        v-if="!storePageTitle"
        class="mb-0"
        width="15%"
        height="1.5rem"
      />
      <h2 v-else>
        <slot name="title">
          {{ $t(storePageTitle) }}
        </slot>
      </h2>
      <app-icon-with-action
        v-if="storeHelpUrl && !hideHelper"
        :href="storeHelpUrl"
        :hoverEffect="false"
        :title="storeHelpDescription"
        target="_blank"
        rel="noopener"
        class="title-question-btn"
      >
        <i class="fas fa-circle-question"></i>
      </app-icon-with-action>
    </div>
    <div class="breadcrumbs">
      <slot name="breadcrumbs">
        <b-skeleton
          v-if="!storeBreadcrumbs.length"
          class="m-2 mb-0"
          width="25%"
        />
        <app-breadcrumb
          v-else
          :items="storeBreadcrumbs"
        />
      </slot>
    </div>
  </b-overlay>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { BackLinkStorageService } from '@/services/backLinkStorage.service'
import AppBreadcrumb from '@/components/htmlHelpers/AppBreadcrumb'
import AppIconWithAction from '@/components/htmlHelpers/AppIconWithAction'

export default {
  name: 'ThePageHeader',
  components: { AppIconWithAction, AppBreadcrumb },
  props: {
    forceDisplay: {
      type: Boolean,
      default: false
    },
    localPlacement: {
      type: Boolean,
      default: false
    },
    hideHelper: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('InfoStore', ['backLink']),
    ...mapGetters('PageHeaderStore', ['isGlobalPageHeaderHidden']),
    ...mapState({
      backLinkEnabled: state => state.PageHeaderStore.backLinkEnabled,
      storePageTitle: state => state.PageHeaderStore.pageTitle,
      storeHelpUrl: state => state.PageHeaderStore.helpUrl,
      storeHelpDescription: state => state.PageHeaderStore.helpDescription,
      storeBreadcrumbs: function (state) {
        const breadcrumbs = state.PageHeaderStore.breadcrumbs.map(crumb => {
          return { ...crumb, ...{ text: this.$t(crumb.text) } }
        })

        if (this.backLinkEnabled) {
          const backLinkId = this.$route.query?.backLinkId
          const backLinkStorage = BackLinkStorageService.getOrCreate()
          if (backLinkStorage[backLinkId]) {
            // Check if there is a correspondent backLink in the sessionStorage (USe for the new Atlas backLinks)
            breadcrumbs.unshift({
              to: backLinkStorage[backLinkId].linkUrl || '',
              text: this.$t(backLinkStorage[backLinkId].linkName || '')
            })
          } else if (this.backLink) {
            // Check if there is a correspondent backLink in the atlasInfo (USe for old Atlas backLinks)
            breadcrumbs.unshift({
              href: this.$atlasHelpers.getAtlasRelativeLink(this.backLink.linkUrl),
              text: this.$t(this.backLink.linkName)
            })
          }
        }
        return breadcrumbs
      }
    }),
    isPageHeaderDisplayed () {
      return !!((this.storePageTitle !== null || this.storeHelpUrl !== null) && !this.isGlobalPageHeaderHidden)
    }
  }
}
</script>

<style scoped lang="scss">
.page-header::v-deep {
  box-shadow: $box-light-gray-shadow;
  border-radius: 4px;
  margin: 1rem 1rem 0;

  &:not(.local-placement) {
    .b-overlay {
      margin: -1rem -1rem 0;
    }
  }

  &.hidden {
    display: none;
  }

  &.local-placement {
    margin: 0.25rem 0 0.5rem;
  }

  .title {
    padding: rem-calc(10 12);
    background-color: var(--scheme-top-nav-active-bg-dk, $white);
    color: var(--scheme-top-nav-active-txt, $black);
    display: flex;
    justify-content: space-between;
    align-items: center;
    @include border-radius(.25rem .25rem 0 0);

    h2 {
      margin-bottom: 0;
      font-size: $font-l;
      color: inherit;
      font-family: $font-lato-bold
    }

    .icon-with-action {
      font-size: $font-m;
    }
  }

  .breadcrumbs {
    overflow: hidden;
    background-color: $white;
    @include border-radius(0 0 .25rem .25rem);
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
}

@media print {
  .page-header {
    display: flex;
    justify-content: center;
    margin: 0 1rem !important;
    page-break-after: avoid;

    &.hidden {
      display: none !important;
    }

    .title-question-btn, .breadcrumbs {
      display: none !important;
    }

    .title {
      font-size: 12pt !important;
      color: $gray-600;
      padding-bottom: 0;
      background: none;
    }
  }
}
</style>
